import React, { useContext, useEffect, useState } from "react";
import SearchForm from "../SearchForm";
import { GlobalContext } from "../../contexts/globalContext";
import styled, { keyframes } from "styled-components";
import { getRandomInt } from "../../Utils";

const opts = ["Find", "Offer", "Intercity", "Local"];

const HeroSection = () => {
  const [changingText, setChangingText] = useState("Find");
  const { searchFields, setSearchFields } = useContext(GlobalContext);

  useEffect(() => {
    const interval = setInterval(() => {
      setChangingText(opts[getRandomInt(opts.length)]);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <HeroContainer>
      <BackgroundOverlay />
      <ContentWrapper>
        <HeroTextContent>
          <GreetingBadge>Ride in Style</GreetingBadge>
          <MainHeading>
            <GradientText>{changingText}</GradientText> Your Perfect <br />
            Carpool Ride
          </MainHeading>
          <SubHeading>
            Experience premium intercity and local travel across Pakistan.
            <br /> Connect with drivers, save cost, and travel with comfort.
          </SubHeading>
          <AndroidLink
            href="https://play.google.com/store/apps/details?id=com.gosawari.app&pcampaignid=web_share"
            target="_blank"
            rel="noopener noreferrer"
          >
            <PlayIcon src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Get it on Google Play" />
          </AndroidLink>
        </HeroTextContent>

        <GlassSearchWrapper>
          <SearchForm
            searchFields={searchFields}
            setSearchFields={setSearchFields}
          />
        </GlassSearchWrapper>
      </ContentWrapper>
    </HeroContainer>
  );
};

export default HeroSection;

// Animations
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// Styled Components (Light Theme)
const HeroContainer = styled.div`
  position: relative;
  min-height: 85vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #f0fdfa; /* Very light teal */
  background: linear-gradient(135deg, #f0fdfa 0%, #e6fffa 100%);
  padding: 4rem 2rem;

  @media (max-width: 1024px) {
    min-height: 70vh;
    padding: 6rem 1.5rem 4rem;
  }

  @media (max-width: 768px) {
    min-height: auto;
    padding: 6rem 1rem 4rem;
  }
`;

const BackgroundOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(
    circle at 50% 50%,
    rgba(5, 71, 82, 0.05) 0%, /* Primary color wash */
    transparent 70%
  );
  z-index: 1;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3rem;

  @media (max-width: 768px) {
    gap: 2rem;
  }
`;

const HeroTextContent = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 800px;
  animation: ${float} 6s ease-in-out infinite;
`;

const GreetingBadge = styled.span`
  background: rgba(5, 71, 82, 0.1);
  color: #054752;
  padding: 0.5rem 1.5rem;
  border-radius: 50px;
  border: 1px solid rgba(5, 71, 82, 0.2);
  font-weight: 600;
  font-size: 0.9rem;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: 0.4rem 1.2rem;
    margin-bottom: 1rem;
  }
`;

const MainHeading = styled.h1`
  font-size: 4.5rem;
  font-weight: 800;
  line-height: 1.15;
  color: #054752; /* Primary Dark Teal */
  margin: 0;
  letter-spacing: -2px;

  @media (max-width: 1024px) {
    font-size: 3.5rem;
    letter-spacing: -1.5px;
  }

  @media (max-width: 768px) {
    font-size: 2.5rem;
    letter-spacing: -1px;
    line-height: 1.2;
  }

  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const GradientText = styled.span`
  color: #00897b; /* Lighter teal */
  background: linear-gradient(to right, #00897b, #004d40);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  display: inline-block;
`;

const SubHeading = styled.p`
  color: #556c70; /* Muted teal/grey */
  font-size: 1.25rem;
  line-height: 1.6;
  margin-top: 1.5rem;
  max-width: 600px;
  font-weight: 500;

  @media (max-width: 1024px) {
    font-size: 1.1rem;
    margin-top: 1rem;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0 1rem;
    margin-top: 0.75rem;
  }
`;

const AndroidLink = styled.a`
  display: inline-block;
  margin-top: 2rem;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    margin-top: 1.5rem;
  }
`;

const PlayIcon = styled.img`
  height: 50px;
  width: auto;

  @media (max-width: 768px) {
    height: 45px;
  }
`;

const GlassSearchWrapper = styled.div`
  width: 100%;
  max-width: 900px;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 24px;
  padding: 2rem;
  box-shadow: 0 20px 40px rgba(5, 71, 82, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 30px 60px rgba(5, 71, 82, 0.15);
  }

  @media (max-width: 1024px) {
    max-width: 800px;
    padding: 1.5rem;
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
    border-radius: 20px;
    box-shadow: 0 10px 30px rgba(5, 71, 82, 0.1);
  }

  @media (max-width: 480px) {
    padding: 1rem;
    border-radius: 16px;
  }
`;
